import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { notify } from '@/lib/notify';
import { StoreSelect } from '../../components/StoreSelect';
import { useSetProductBrand, useStoreBrands, type StoreBrandRow } from '../../settings/useStoreBrands';

function manufacturerDataMissing(row: Pick<StoreBrandRow, 'legalName' | 'postalAddress' | 'contactEmail'> | undefined): boolean {
    if (!row) return true;
    return !row.legalName.trim() || !row.postalAddress.trim() || !row.contactEmail.trim();
}

export function CatalogProductBrandField({
    productId,
    current,
    pendingEditorial = false,
}: {
    productId: number;
    current: string;
    /** La marca editorial antigua sigue tapando al fabricante hasta que se guarda este control. */
    pendingEditorial?: boolean;
}) {
    const brands = useStoreBrands();
    const save = useSetProductBrand();
    const [brand, setBrand] = useState(current);
    const [custom, setCustom] = useState('');
    const [seen, setSeen] = useState(current);
    if (seen !== current) {
        setSeen(current);
        setBrand(current);
        setCustom('');
    }
    const names = (brands.data ?? []).map((row) => row.name);
    const known = names.includes(brand);
    const chosen = brand === '__otra' ? custom.trim() : brand.trim();
    const selectValue = known || brand === '' ? brand : '__otra';

    return (
        <div className="space-y-2">
            <div className="space-y-1">
                <Label htmlFor="ff-fabricante">Marca (fabricante)</Label>
                <StoreSelect
                    id="ff-fabricante"
                    ariaLabel="Marca (fabricante)"
                    value={selectValue}
                    placeholder="Sin asignar"
                    onChange={setBrand}
                    options={[
                        { value: '', label: 'Sin asignar' },
                        ...names.map((name) => ({ value: name, label: name })),
                        { value: '__otra', label: 'Otra marca' },
                    ]}
                />
                {(brand === '__otra' || (!known && brand !== '')) ? (
                    <Input
                        value={brand === '__otra' ? custom : brand}
                        aria-label="Otra marca"
                        placeholder="Nombre de la marca"
                        onChange={(event) => {
                            setBrand('__otra');
                            setCustom(event.target.value);
                        }}
                    />
                ) : null}
            </div>
            <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={save.isPending || !chosen || (!pendingEditorial && chosen === current)}
                onClick={() => {
                    save.mutate(
                        { productId, brand: chosen },
                        {
                            onSuccess: () => notify({ type: 'ok', text: 'Marca del producto guardada' }),
                            onError: (error) => notify({ type: 'err', text: error.message }),
                        },
                    );
                }}
            >
                Guardar marca
            </Button>
        </div>
    );
}

export function BrandCompliance({ brandName }: { brandName: string }) {
    const brands = useStoreBrands();
    const name = brandName.trim();
    const row = (brands.data ?? []).find((item) => item.name.toLowerCase() === name.toLowerCase());
    const href = `/store/settings/brands${name ? `?marca=${encodeURIComponent(name)}` : ''}`;
    const missing = !name || manufacturerDataMissing(row);

    return (
        <div className="space-y-1 text-sm">
            {!name ? (
                <p>Sin marca asignada.</p>
            ) : missing ? (
                <p>Faltan datos del fabricante{name ? ` de ${name}` : ''}.</p>
            ) : (
                <>
                    <p>{row!.legalName}</p>
                    <p>{row!.postalAddress}</p>
                    <p>{row!.contactEmail}</p>
                    {row!.euResponsibleName ? (
                        <p className="text-muted-foreground">Responsable en la UE: {row!.euResponsibleName}</p>
                    ) : null}
                </>
            )}
            <p>
                <Link className="text-primary underline" to={href}>
                    Marcas de tienda
                </Link>
            </p>
        </div>
    );
}
